import { supabase } from './supabase';
import { moduloCanonico } from './modulos';

// ─── Body system classification ───────────────────────────────────────────────

export const BODY_SYSTEMS = [
  { id: 'respiratorio',  label: 'Respiratorio',        keywords: ['respirat', 'pulmon', 'pulmón', 'ventilaci', 'disnea', 'saturaci', 'oxígeno', 'oxigeno', 'sdra', 'neumonía', 'neumonia'] },
  { id: 'cardiovascular',label: 'Cardiovascular',      keywords: ['cardiaco', 'cardíaco', 'hemodin', 'shock', 'presion', 'tensión', 'arritmia', 'corazon', 'corazón', 'gasto card'] },
  { id: 'neurologico',   label: 'Neurológico',         keywords: ['neurol', 'conciencia', 'cognitiv', 'confus', 'delirium', 'convuls', 'cerebro', 'cerebral', 'encefalop'] },
  { id: 'renal',         label: 'Renal/Urinario',      keywords: ['renal', 'urinario', 'urinaria', 'oligur', 'diuresi', 'riñon', 'riñón', 'uropatía'] },
  { id: 'digestivo',     label: 'Gastrointestinal',    keywords: ['gástric', 'gastro', 'intestin', 'digest', 'hepát', 'nausea', 'vomit', 'abdomen', 'hígado', 'higado'] },
  { id: 'infeccioso',    label: 'Infeccioso/Inmune',   keywords: ['infeccion', 'infección', 'sepsis', 'hipertermia', 'bacteremi', 'fiebre', 'viral', 'bacteriana'] },
  { id: 'nutricional',   label: 'Nutrición/Metabólico',keywords: ['nutric', 'metabol', 'glucos', 'diabetes', 'glucemia', 'desnutric', 'malnutric'] },
  { id: 'psicosocial',   label: 'Psicosocial',         keywords: ['ansiedad', 'depresion', 'depresión', 'miedo', 'afrontamiento', 'estrés', 'angustia', 'psicol'] },
  { id: 'dolor',         label: 'Dolor/Confort',       keywords: ['dolor', 'confort', 'analgesia', 'analgésico', 'doloros'] },
  { id: 'piel',          label: 'Integridad Cutánea',  keywords: ['piel', 'ulcera', 'úlcera', 'herida', 'lesion cutánea', 'integrid'] },
  { id: 'movilidad',     label: 'Movilidad/Funcional', keywords: ['movil', 'movilid', 'caida', 'caída', 'fractura', 'muscul', 'deambulac'] },
];

export function classifyDx(dxText) {
  if (!dxText) return null;
  const lower = dxText.toLowerCase();
  for (const sys of BODY_SYSTEMS) {
    if (sys.keywords.some(kw => lower.includes(kw))) return sys.id;
  }
  return null;
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export async function getMyJourney(userId) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from('paes')
    .select('id, modo, titulo, created_at, datos')
    .eq('user_id', userId)
    .order('created_at', { ascending: true })
    .limit(50);
  if (error) throw error;
  return (data || []).map(p => {
    const fd = p.datos?.formData;
    const dxMedico = p.modo === 'rapido'
      ? fd?.rapido?.dx_medico || null
      : fd?.anamnesis?.dx_medico || null;
    return {
      id: p.id,
      modo: p.modo,
      titulo: p.titulo,
      createdAt: p.created_at,
      dxMedico,
      sistema: classifyDx(dxMedico),
    };
  });
}

export async function getMyReflectionCount(userId) {
  if (!userId) return 0;
  const { count, error } = await supabase
    .from('bitacora_entradas')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId);
  if (error) throw error;
  return count || 0;
}

export async function getMyConsistency(userId) {
  if (!userId) return [];
  const from = new Date(Date.now() - 28 * 86_400_000).toISOString();

  const [{ data: paesData }, { data: bitacoraData }, { data: eventosData }] =
    await Promise.all([
      supabase.from('paes').select('created_at').eq('user_id', userId).gte('created_at', from),
      supabase.from('bitacora_entradas').select('created_at').eq('user_id', userId).gte('created_at', from),
      supabase.from('eventos_v2').select('created_at').eq('user_id', userId).gte('created_at', from),
    ]);

  const allDates = [
    ...(paesData || []),
    ...(bitacoraData || []),
    ...(eventosData || []),
  ].map(r => r.created_at.slice(0, 10));

  return [...new Set(allDates)];
}

export async function getMyModulesUsed(userId) {
  if (!userId) return new Set();
  const { data } = await supabase
    .from('eventos_v2')
    .select('modulo, evento')
    .eq('user_id', userId)
    .not('modulo', 'is', null);
  // Normaliza a nombre canónico para que un milestone matchee sin importar si la
  // fila es histórica (nombre viejo) o nueva.
  return new Set((data || []).map(e => moduloCanonico(e.modulo)));
}

// ─── Aggregate stats for milestone evaluation ─────────────────────────────────

export async function getMyFullStats(userId) {
  if (!userId) return null;

  const [journey, reflectionCount, activeDays, modulesUsed] = await Promise.all([
    getMyJourney(userId),
    getMyReflectionCount(userId),
    getMyConsistency(userId),
    getMyModulesUsed(userId),
  ]);

  const systemsWorked = new Set(journey.map(p => p.sistema).filter(Boolean));

  return {
    paeRapidoCount:    journey.filter(p => p.modo === 'rapido').length,
    paeDetalladoCount: journey.filter(p => p.modo === 'detallado').length,
    totalPaes:         journey.length,
    bitacoraCount:     reflectionCount,
    modulesUsed,
    systemsWorked,
    activeDaysCount:   activeDays.length,
    journey,
    activeDays,
  };
}
